import {makeAutoObservable, reaction, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IUserStore} from "data/stores/user/user.store";
import type {IContextualPromptBannerStore} from "data/stores/contextual_prompt_banner/contextual_prompt_banner.store";
import {RequestState} from "data/enums";
import {noop} from "lodash";
import {type IPrompt} from "data/providers/api/contextual_prompt_banner/contextual_prompt_banner.provider";

export interface IContextualPromptBannerController extends ViewController {
	get promptContent(): IPrompt[];
	get uid(): string;
	get isOpen(): boolean;

	remove: (game: IPrompt["game"]) => void;
}

@injectable()
export class ContextualPromptBannerController implements IContextualPromptBannerController {
	private _disposers: (() => void)[] = [];
	@observable _isOpen: boolean = false;
	@observable _requestState: RequestState = RequestState.IDLE;
	private _timeout: ReturnType<typeof setTimeout> | null = null;

	constructor(
		@inject(Bindings.ContextualPromptBannerStore)
		private _contextualPromptBannerStore: IContextualPromptBannerStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	get uid() {
		return this._userStore.uid || "";
	}

	get isOpen(): boolean {
		return this._isOpen;
	}

	get promptContent() {
		return this._contextualPromptBannerStore.promptContent;
	}

	init(param: void) {
		this._disposers = [
			reaction(
				() => this.promptContent,
				(project) => {
					this._isOpen = !!project.length;

					if (project.length) {
						this._timeout = setTimeout(() => {
							this._isOpen = false;
							void this._contextualPromptBannerStore
								.decline(this._userStore.uid)
								.catch(noop);
						}, 10000);
					}
				},
				{fireImmediately: true}
			),
		];
	}

	dispose() {
		this._disposers.forEach((disposer) => disposer());
	}

	remove = (game: IPrompt["game"]) => {
		this._contextualPromptBannerStore.remove(game);
		void this._contextualPromptBannerStore.decline(this._userStore.uid).catch(noop);
		if (this._timeout) {
			clearTimeout(this._timeout);
		}
	};
}
